import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { perfilamientoService } from "@/features/perfilamiento/services/perfilamiento.service";
import type { PerfilProfileDto } from "@/features/perfilamiento/services/perfilamiento.types";
import { Button } from "@/shared/components/ui/Button";
import { usePerfilamientoError } from "@/features/perfilamiento/components/PerfilamientoErrorModal";
import { CheckCircleIcon } from "@/shared/components/ui/icons/CheckCircleIcon";
import { WarningTriangleIcon } from "@/shared/components/ui/icons/WarningTriangleIcon";
import logoNegroSrc from "@/assets/LogoNegro.png";

type ResultLocationState = {
  profile?: PerfilProfileDto | null;
  finalScore?: number | null;
  archetype?: number | null;
  justCompleted?: boolean;
};

type ResultState = {
  profile: PerfilProfileDto | null;
  finalScore: number | null;
  archetype: number | null;
};

// El avatar llega desde datos persistidos. Solo se aceptan URLs HTTPS del
// storage conocido; no se valida el host buscando un fragmento en el string.
const ALLOWED_AVATAR_HOSTS = new Set([
  "synergyapex.blob.core.windows.net",
]);

function parseSafeAvatarUrl(value: string): URL | null {
  try {
    const parsed = new URL(value);
    const hostname = parsed.hostname.toLowerCase();

    if (parsed.protocol !== "https:" || !ALLOWED_AVATAR_HOSTS.has(hostname)) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function PerfilamientoResultPage() {
  const { processId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("perfilamiento");
  const { showError, clearError } = usePerfilamientoError();
  const [state, setState] = useState<ResultState | null>(() => {
    const initial = location.state as ResultLocationState | null;

    return {
      profile: initial?.profile ?? null,
      finalScore: initial?.finalScore ?? null,
      archetype: initial?.archetype ?? null,
    };
  });
  const [isLoading, setIsLoading] = useState(!state?.profile);
  const [error, setError] = useState<string | null>(null);
  const [isAdvisorySending, setIsAdvisorySending] = useState(false);
  const [advisoryError, setAdvisoryError] = useState<string | null>(null);
  const [isRestarting, setIsRestarting] = useState(false);
  const [advisoryVisible, setAdvisoryVisible] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isFreshCompletion] = useState<boolean>(() => {
    const initial = location.state as ResultLocationState | null;
    return Boolean(initial?.justCompleted);
  });

  useEffect(() => {
    // El proveedor del modal vive fuera del router; evita arrastrar un error
    // de una pantalla anterior al resultado o a una pregunta de desempate.
    clearError();

    if (!processId || state?.profile) return;

    const currentProcessId = processId as string;

    async function loadResult() {
      setIsLoading(true);
      setError(null);

      const result = await perfilamientoService.getProceso(
        currentProcessId,
        i18n.language,
      );

      if (!result.success) {
        setError(result.message);
        showError(result.message, result.errorCode);
        setIsLoading(false);
        return;
      }

      const process = result.data.process;

      if (!process.profile) {
        navigate("/perfilamiento", { replace: true });
        return;
      }

      setState({
        profile: process.profile,
        finalScore: process.finalScore,
        archetype: process.archetype,
      });
      setIsLoading(false);
    }

    void loadResult();
  }, [clearError, i18n.language, navigate, processId, showError, state?.profile]);

  const profile = state?.profile ?? null;
  useEffect(() => {
    const avatar = profile?.avatar?.trim() ?? "";

    if (!avatar) {
      setAvatarUrl(null);
      return;
    }

    const parsedAvatar = parseSafeAvatarUrl(avatar);

    if (!parsedAvatar) {
      setAvatarUrl(null);
      return;
    }

    if (parsedAvatar.search) {
      setAvatarUrl(parsedAvatar.toString());
      return;
    }

    let cancelled = false;

    async function resolveAvatar() {
      const result = await perfilamientoService.getAvatarResource(avatar);

      if (!cancelled && result.success) {
        setAvatarUrl(result.data.url ?? null);
      }
    }

    void resolveAvatar();

    return () => {
      cancelled = true;
    };
  }, [profile?.avatar]);

  const isVideoAvatar = Boolean(
    avatarUrl && /\.(mp4|webm|ogg)(?:$|[?#])/i.test(avatarUrl),
  );

  async function handleAdvisory() {
    if (!processId) return;

    setAdvisoryError(null);
    clearError();
    setIsAdvisorySending(true);

    const result = await perfilamientoService.advisory(
      processId,
      i18n.language,
    );

    setIsAdvisorySending(false);

    if (!result.success) {
      setAdvisoryError(result.message);
      showError(result.message, result.errorCode);
      return;
    }

    setAdvisoryVisible(true);
  }

  async function handleRestart() {
    if (!processId || isRestarting) return;

    setIsRestarting(true);
    setError(null);

    const result = await perfilamientoService.start(
      {
        channel: 1,
        consentAccepted: true,
        consentVersion: "1.0",
        previousProcessId: processId,
      },
      i18n.language,
    );

    setIsRestarting(false);

    if (!result.success) {
      setError(result.message);
      showError(result.message, result.errorCode);
      return;
    }

    navigate(`/perfilamiento/filtro/${result.data.processId}`, {
      state: { filterQuestion: result.data.filterQuestion },
    });
  }

  return (
    <section className="relative flex h-[100svh] w-full justify-center overflow-y-auto bg-white shadow-[2px_3px_9px_0_rgba(28,48,66,0.10)]">
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[clamp(180px,29svh,260px)] bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,rgba(0,122,204,0.5)_55%,#00558E_100%)]" />
      <div className="relative flex min-h-full w-full max-w-[1440px] rounded-[14px] bg-transparent md:px-[20px] md:pb-[48px] md:pt-[24px]">
        <div className="relative flex h-full w-full flex-col items-center justify-between px-5 py-6 text-center md:px-0 md:py-0">
          <div className="w-[176px] md:w-[252px]">
            <img alt="SynerGy" className="h-auto w-full" src={logoNegroSrc} />
          </div>

          {isLoading ? (
            <p className="mt-10 text-sm text-[var(--color-text-muted)]">
              {t("result.loading")}
            </p>
          ) : null}

          {error ? (
            <p className="mt-10 rounded-2xl border border-[#ffd4d8] bg-[#fff5f6] px-6 py-4 text-sm text-[#8a1f2d]">
              {error}
            </p>
          ) : null}

          {profile ? (
            <div className="flex w-full max-w-[780px] flex-1 flex-col items-center justify-center">
              <p className="mt-8 font-[var(--font-base)] text-[16px] font-normal uppercase leading-[20px] text-[#000]">
                {t("result.investorHeadline")}
              </p>

              <h1 className="mt-2 font-[var(--font-heading)] text-[46px] font-medium leading-[52px] text-[#003D66]">
                {profile.name}
              </h1>

              <p className="mt-4 max-w-[440px] font-[var(--font-heading)] text-[22px] font-medium leading-[26px] text-[#05141F]">
                <span className="block">“{profile.resultText}”</span>
              </p>

              <div className="mt-8 flex w-full items-center justify-center">
                {avatarUrl ? (
                  isVideoAvatar ? (
                    <video
                      className="h-[281px] w-[300px] max-w-full object-contain md:h-[281px] md:w-[300px]"
                      autoPlay
                      loop
                      muted
                      playsInline
                      aria-label={profile.name}
                    >
                      <source src={avatarUrl} type="video/mp4" />
                    </video>
                  ) : (
                    <img
                      alt={profile.name}
                      className="h-[236px] w-[340px] max-w-full object-contain md:aspect-[85/59] md:h-[236px] md:w-[340px]"
                      src={avatarUrl}
                    />
                  )
                ) : (
                  <div className="flex h-[236px] w-[340px] max-w-full items-center justify-center rounded-[32px] bg-white shadow-[0_16px_40px_rgba(5,20,31,0.1)] md:aspect-[85/59] md:h-[236px] md:w-[340px]">
                    <span className="text-4xl font-semibold text-[var(--color-brand-primary)]">
                      {profile.name.slice(0, 1)}
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-8 flex w-full max-w-[600px] flex-row items-center justify-center gap-3 pb-1">
                {isFreshCompletion ? (
                  <Button
                    className="flex h-[48px] flex-1 items-center justify-center rounded-[8px] bg-[#0066AA] px-5 py-2 text-white hover:bg-[#005c99]"
                    onClick={() => void handleAdvisory()}
                    fullWidth={false}
                    variant="primary"
                    disabled={isAdvisorySending}
                  >
                    {isAdvisorySending
                      ? t("result.advisorySending")
                      : t("result.restart")}
                  </Button>
                ) : null}

                <button
                  className={[
                    "flex h-[48px] items-center justify-center rounded-[8px] px-5 py-2 font-[var(--font-base)] text-[1rem] font-medium",
                    isFreshCompletion
                      ? "flex-1 border border-[#6AB9E5] text-[#000]"
                      : "w-full max-w-[353px] bg-[#0066AA] text-white hover:bg-[#005c99]",
                  ].join(" ")}
                  onClick={() => {
                    if (isFreshCompletion) {
                      navigate("/perfilamiento", { replace: true })
                      return
                    }

                    void handleRestart()
                  }}
                  disabled={isRestarting}
                  type="button"
                >
                  {isFreshCompletion
                    ? t("result.advisoryCta")
                    : t("result.restartCta")}
                </button>
              </div>

            </div>
          ) : null}
        </div>

        {advisoryVisible ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(5,20,31,0.45)] px-4">
            <div className="w-full max-w-[340px] rounded-[12px] bg-[#EEF7FC] px-8 py-8 text-center shadow-[0_20px_60px_rgba(5,20,31,0.18)]">
              <div className="mx-auto flex h-9 w-9 scale-[2.25] items-center justify-center">
                <CheckCircleIcon />
              </div>

              <h2 className="mt-5 text-[26px] font-semibold leading-6 text-[var(--color-text-primary)]">
                {t("filter.advisoryTitle")}
              </h2>

              <p className="mt-4 text-[16px] leading-6 text-black">
                {t("filter.advisoryDescription")}
              </p>

              <Button
                className="mt-12 !bg-[#1FC16B]"
                onClick={() => navigate("/dashboard", { replace: true })}
              >
                {t("filter.advisoryAccept")}
              </Button>
            </div>
          </div>
        ) : null}

        {advisoryError ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(5,20,31,0.45)] px-4">
            <div className="w-full max-w-[420px] rounded-[12px] bg-[#EEF7FC] px-8 py-8 text-center shadow-[0_20px_60px_rgba(5,20,31,0.18)]">
              <div className="mx-auto flex h-9 w-9 scale-[2.25] items-center justify-center">
                <WarningTriangleIcon />
              </div>

              <h2 className="mt-5 text-[26px] font-semibold leading-6 text-[var(--color-text-primary)]">
                {t("questionnaire.errorModal.title")}
              </h2>

              <p className="mt-4 whitespace-pre-line text-[16px] leading-6 text-black">
                {advisoryError}
              </p>

              <Button
                className="mt-12 !bg-[#FB3748]"
                onClick={() => setAdvisoryError(null)}
              >
                {t("questionnaire.errorModal.accept")}
              </Button>
            </div>
          </div>
        ) : null}

      </div>
    </section>
  );
}
